import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Spinner, Alert } from "react-bootstrap";
import ReactSelect from "react-select";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useITeachConfig } from "../../_context/PresetContext";
import LessonPlanCoverageReportService, {
  Option,
  TopicOption,
  CoverageReport,
  CoverageCentre,
  CoverageBatch,
  CellMetrics,
  CoverageQuery,
} from "../../_services/LessonPlanCoverageReportService";
import "./index.css";

type RS = { value: number; label: string };
// Topic options carry their course so identical topic names across courses stay
// distinguishable (grouped in the menu, course-prefixed on the selected chips).
type RSTopic = RS & { courseId: number; courseName: string };
const toRS = (o: Option): RS => ({ value: o.id, label: o.name });
const toRSTopic = (t: TopicOption): RSTopic => ({
  value: t.id, label: t.name, courseId: t.courseId, courseName: t.courseName,
});
const idsOf = (v: RS[] | null | undefined): number[] => (v || []).map((x) => x.value);

// Attendance metrics shown in the expanded subject detail strip. Headers stay
// abbreviated to keep the strip narrow — except the sessions count, spelled out
// to match the export and to not read as a bare "P".
const ATT: Array<{ h: string; k: keyof CellMetrics }> = [
  { h: "Sessions Planned", k: "p" },
  { h: "Total Held", k: "totalHeld" },
  { h: "Held", k: "held" },
  { h: "Exam", k: "exam" },
  { h: "Diff (P-H)", k: "diffPH" },
  { h: "Swap", k: "swapped" },
  { h: "Canc", k: "cancelled" },
  { h: "Add", k: "additional" },
  { h: "Hol", k: "holiday" },
];

const emptyCell = (): CellMetrics => ({
  planned: 0, covered: 0, difference: 0, p: 0, held: 0, exam: 0,
  swapped: 0, additional: 0, cancelled: 0, holiday: 0, totalHeld: 0, diffPH: 0,
});

// Coverage % + tone for the compact chip.
const coverageChip = (c: CellMetrics) => {
  const pct = c.planned > 0 ? Math.round((c.covered / c.planned) * 100) : 0;
  const tone = c.planned === 0 ? "none" : pct >= 80 ? "good" : pct >= 40 ? "warn" : "bad";
  return { pct, tone, label: `${c.covered}/${c.planned}` };
};

const Chip: React.FC<{ c: CellMetrics }> = ({ c }) => {
  const chip = coverageChip(c);
  return (
    <span className={`lpcr-chip lpcr-chip-${chip.tone}`}>
      {chip.label}{c.planned > 0 ? ` · ${chip.pct}%` : ""}
    </span>
  );
};

/**
 * One course's coverage grid, master-detail:
 *   - Main grid is a fixed 5 columns (Centre/Batch/Instructor/Coverage/Subjects) —
 *     never scrolls horizontally regardless of subject count.
 *   - A batch row expands to show its subjects as rows (coverage chip + attendance strip).
 */
const CoverageTable: React.FC<{
  topics: Option[];
  centres: CoverageCentre[];
  isBatchOpen: (batchId: number) => boolean;
  toggleBatch: (batchId: number) => void;
  showCoverage: boolean;
  /** Attendance columns enabled for this tenant, already filtered — may be empty. */
  attCols: typeof ATT;
}> = ({ topics, centres, isBatchOpen, toggleBatch, showCoverage, attCols }) => {
  if (!centres.length) {
    return <div className="lpcr-empty">No data for this course with the selected filters.</div>;
  }

  // Visible main-grid columns: Centre, Batch, Instructor, [Coverage], Subjects.
  const mainCols = 4 + (showCoverage ? 1 : 0);

  const detailRow = (batch: CoverageBatch) => (
    <tr className="lpcr-detail">
      <td colSpan={mainCols}>
        <table className="lpcr-detail-inner">
          <thead>
            <tr>
              <th className="lpcr-di-subject">Subject</th>
              <th className="lpcr-di-instr">Instructor</th>
              {showCoverage && <th>Coverage</th>}
              {attCols.map((a) => <th key={a.k}>{a.h}</th>)}
            </tr>
          </thead>
          <tbody>
            {topics.map((t) => {
              const c = batch.perTopic[String(t.id)] || emptyCell();
              const instr = batch.instructorPerTopic?.[String(t.id)] || "";
              return (
                <tr key={t.id}>
                  <td className="lpcr-di-subject">{t.name}</td>
                  <td className="lpcr-di-instr" title={instr}>{instr || "--"}</td>
                  {showCoverage && <td><Chip c={c} /></td>}
                  {attCols.map((a) => <td key={a.k} className="lpcr-num">{Number(c[a.k]) || 0}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </td>
    </tr>
  );

  return (
    <div className="lpcr-md-wrap">
      <table className="lpcr-md-table">
        <thead>
          <tr>
            <th>Centre</th>
            <th>Batch</th>
            <th>Instructor</th>
            {showCoverage && <th>Coverage</th>}
            <th>Subjects</th>
          </tr>
        </thead>
        <tbody>
          {centres.map((centre) => (
            <React.Fragment key={centre.schoolId}>
              {centre.batches.map((batch, i) => {
                const open = isBatchOpen(batch.batchId);
                return (
                  <React.Fragment key={batch.batchId}>
                    <tr className={open ? "lpcr-md-row lpcr-open" : "lpcr-md-row"}>
                      <td>{i === 0 ? centre.schoolName : ""}</td>
                      <td>{batch.batchName}</td>
                      <td className="lpcr-instr" title={batch.instructor}>{batch.instructor || "--"}</td>
                      {showCoverage && <td><Chip c={batch.overall} /></td>}
                      <td>
                        <button
                          type="button"
                          className="lpcr-subjects-toggle"
                          onClick={() => toggleBatch(batch.batchId)}
                          aria-expanded={open}
                        >
                          <span className="lpcr-caret">{open ? "▾" : "▸"}</span>
                          {topics.length} Subject{topics.length === 1 ? "" : "s"}
                        </button>
                      </td>
                    </tr>
                    {open && detailRow(batch)}
                  </React.Fragment>
                );
              })}
              <tr className="lpcr-total-row">
                <td>{centre.schoolName} Total</td>
                <td>--</td>
                <td>--</td>
                {showCoverage && <td><Chip c={centre.total.overall} /></td>}
                <td></td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LessonPlanCoverageReport: React.FC = () => {
  useDocumentTitle("Lesson Plan Coverage Report");

  // Section visibility from iTeachAppConfig.lessonPlanCoverageReport (default-on).
  const { config } = useITeachConfig();
  const lpcr = config.lessonPlanCoverageReport;
  const showCoverage = lpcr?.LessonPlan_Coverage !== false;
  // Group master first, then the per-column switches (both default-on). Same
  // gating the server applies when building the Excel export.
  const showAttendance = lpcr?.Attendance_Sessions !== false;
  const attCols = useMemo(
    () => (showAttendance ? ATT.filter((a) => lpcr?.attendanceColumns?.[a.k] !== false) : []),
    [showAttendance, lpcr],
  );

  // Filter option lists
  const [schools, setSchools] = useState<Option[]>([]);
  const [courses, setCourses] = useState<Option[]>([]);
  const [years, setYears] = useState<Option[]>([]);
  const [topics, setTopics] = useState<TopicOption[]>([]);
  const [batches, setBatches] = useState<Option[]>([]);
  const [instructors, setInstructors] = useState<Option[]>([]);

  // Selections
  const [selSchools, setSelSchools] = useState<RS[]>([]);
  const [selCourses, setSelCourses] = useState<RS[]>([]);
  const [selYear, setSelYear] = useState<RS | null>(null);
  const [selTopics, setSelTopics] = useState<RSTopic[]>([]);
  const [selBatches, setSelBatches] = useState<RS[]>([]);
  const [selInstructors, setSelInstructors] = useState<RS[]>([]);
  const [reportBy, setReportBy] = useState<0 | 1>(0);

  const [report, setReport] = useState<CoverageReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // View controls
  const [openBatches, setOpenBatches] = useState<Set<number>>(new Set());
  const [filtersOpen, setFiltersOpen] = useState(true);

  const allBatchIds = useMemo(
    () => (report?.courses || []).flatMap((c) => c.centres.flatMap((ct) => ct.batches.map((b) => b.batchId))),
    [report],
  );
  const isBatchOpen = (id: number) => openBatches.has(id);
  const toggleBatch = (id: number) =>
    setOpenBatches((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const expandAll = () => setOpenBatches(new Set(allBatchIds));
  const collapseAll = () => setOpenBatches(new Set());

  // Load base filter options once.
  useEffect(() => {
    (async () => {
      try {
        const f = await LessonPlanCoverageReportService.getFilters();
        setSchools(f.schools || []);
        setCourses(f.courses || []);
        setYears(f.years || []);
      } catch (e: any) {
        setError(e?.message || "Failed to load filters.");
      }
    })();
  }, []);

  // When centre(s) change → refetch courses scoped to that centre and drop any
  // selected course no longer offered there. Pruning selCourses cascades to the
  // topics/batches effect below (which resets them when courses go empty).
  useEffect(() => {
    (async () => {
      try {
        const f = await LessonPlanCoverageReportService.getFilters({
          schoolIds: idsOf(selSchools),
        });
        const nextCourses = f.courses || [];
        setCourses(nextCourses);
        setSelCourses((prev) => prev.filter((c) => nextCourses.some((nc) => nc.id === c.value)));
      } catch (e: any) {
        setError(e?.message || "Failed to load courses.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selSchools]);

  // When course(s) change → reload topics (and batches if schools chosen).
  useEffect(() => {
    if (!selCourses.length) {
      setTopics([]);
      setBatches([]);
      setInstructors([]);
      setSelTopics([]);
      setSelBatches([]);
      setSelInstructors([]);
      return;
    }
    (async () => {
      try {
        const f = await LessonPlanCoverageReportService.getFilters({
          courseIds: idsOf(selCourses),
          schoolIds: idsOf(selSchools),
          yearId: selYear?.value,
        });
        const nextTopics = f.topics || [];
        const nextInstructors = f.instructors || [];
        setTopics(nextTopics);
        setBatches(f.batches || []);
        setInstructors(nextInstructors);
        // Drop topic chips whose course is no longer selected.
        setSelTopics((prev) => prev.filter((t) => nextTopics.some((nt) => nt.id === t.value)));
        // Same for instructors — otherwise a centre/course change would keep
        // sending ids that no longer teach anything in scope.
        setSelInstructors((prev) => prev.filter((i) => nextInstructors.some((ni) => ni.id === i.value)));
      } catch (e: any) {
        setError(e?.message || "Failed to load topics/batches/instructors.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selCourses, selSchools, selYear]);

  // With more than one course selected, topic names can collide (e.g. "Physics1"
  // in two courses). Group the menu under course headings and prefix the selected
  // chips with the course name so the user can tell them apart. A single course
  // needs neither, so the list stays flat.
  const multiCourse = selCourses.length > 1;
  const topicOptions = useMemo(() => {
    const all = topics.map(toRSTopic);
    if (!multiCourse) return all;
    const groups: Array<{ label: string; options: RSTopic[] }> = [];
    const byCourse = new Map<number, RSTopic[]>();
    all.forEach((t) => {
      if (!byCourse.has(t.courseId)) {
        byCourse.set(t.courseId, []);
        groups.push({ label: t.courseName || "Unassigned", options: byCourse.get(t.courseId)! });
      }
      byCourse.get(t.courseId)!.push(t);
    });
    return groups;
  }, [topics, multiCourse]);

  // Topic first, course in parentheses — chips truncate left-to-right, so leading
  // with the course name would hide the very thing the user is trying to read.
  const formatTopicLabel = (opt: RSTopic, meta: { context: string }) =>
    meta.context === "value" && multiCourse && opt.courseName
      ? `${opt.label} (${opt.courseName})`
      : opt.label;

  // Default react-select matching only looks at the option label, so typing a
  // course name finds nothing. Match on "<course> <topic>" instead, and require
  // every whitespace-separated term to hit — so "jee phy" narrows to that course's
  // physics topics rather than everything containing either word.
  const filterTopicOption = (opt: { data: RSTopic }, raw: string) => {
    const q = raw.trim().toLowerCase();
    if (!q) return true;
    const hay = `${opt.data.courseName || ""} ${opt.data.label}`.toLowerCase();
    return q.split(/\s+/).every((term) => hay.includes(term));
  };

  const buildQuery = (): CoverageQuery => ({
    courseIds: idsOf(selCourses),
    schoolIds: idsOf(selSchools),
    yearId: selYear?.value,
    topicIds: idsOf(selTopics),
    batchIds: idsOf(selBatches),
    instructorIds: idsOf(selInstructors),
    reportBy,
  });

  const generate = async () => {
    if (!selCourses.length) {
      setError("Please select at least one Course.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await LessonPlanCoverageReportService.getReport(buildQuery());
      setReport(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to generate report.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const exportXls = async () => {
    if (!selCourses.length) {
      setError("Please select at least one Course.");
      return;
    }
    setExporting(true);
    setError(null);
    try {
      const blob = await LessonPlanCoverageReportService.exportReport(buildQuery());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "lesson_plan_coverage.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || "Failed to export.");
    } finally {
      setExporting(false);
    }
  };

  const filterSummary = [
    `Centre: ${selSchools.length ? selSchools.map((s) => s.label).join(", ") : "All"}`,
    `Course: ${selCourses.length ? selCourses.map((s) => s.label).join(", ") : "—"}`,
    selYear ? `Year: ${selYear.label}` : null,
    selTopics.length ? `Topics: ${selTopics.length}` : null,
    selBatches.length ? `Batches: ${selBatches.length}` : null,
    selInstructors.length ? `Instructors: ${selInstructors.length}` : null,
    `By: ${reportBy === 1 ? "Step" : "Step Group"}`,
  ].filter(Boolean).join("  ·  ");

  return (
    <div className="lpcr-page">
      <div className="lpcr-header">
        <h3 className="lpcr-title">Lesson Plan Coverage Report</h3>
      </div>

      {/* Sticky filter + action bar */}
      <div className="lpcr-sticky-bar">
        {filtersOpen ? (
          <div className="lpcr-filters">
            <div className="lpcr-field lpcr-wide">
              <label>Centre</label>
              <ReactSelect isMulti options={schools.map(toRS)} value={selSchools}
                onChange={(v: any) => setSelSchools(v || [])} placeholder="All centres…" classNamePrefix="lpcr-rs" />
            </div>
            <div className="lpcr-field lpcr-wide">
              <label>Course</label>
              <ReactSelect isMulti options={courses.map(toRS)} value={selCourses}
                onChange={(v: any) => setSelCourses(v || [])} placeholder="Select course(s)…" classNamePrefix="lpcr-rs" />
            </div>
            <div className="lpcr-field">
              <label>Year</label>
              <ReactSelect isClearable options={years.map(toRS)} value={selYear}
                onChange={(v: any) => setSelYear(v || null)} placeholder="All years…" classNamePrefix="lpcr-rs" />
            </div>
            <div className="lpcr-field lpcr-wide lpcr-topic-field">
              <label>Topic</label>
              <ReactSelect isMulti options={topicOptions as any} value={selTopics}
                onChange={(v: any) => setSelTopics(v || [])} placeholder="All topics…"
                formatOptionLabel={formatTopicLabel as any}
                filterOption={filterTopicOption as any}
                isDisabled={!selCourses.length} classNamePrefix="lpcr-rs" />
            </div>
            <div className="lpcr-field lpcr-wide">
              <label>Batch</label>
              <ReactSelect isMulti options={batches.map(toRS)} value={selBatches}
                onChange={(v: any) => setSelBatches(v || [])} placeholder="All batches…"
                isDisabled={!selCourses.length} classNamePrefix="lpcr-rs" />
            </div>
            <div className="lpcr-field lpcr-wide">
              <label>Instructor</label>
              <ReactSelect isMulti options={instructors.map(toRS)} value={selInstructors}
                onChange={(v: any) => setSelInstructors(v || [])} placeholder="All instructors…"
                isDisabled={!selCourses.length} classNamePrefix="lpcr-rs" />
            </div>
            <div className="lpcr-field">
              <label>Report By</label>
              <div className="lpcr-radios">
                <Form.Check inline type="radio" name="reportBy" label="Step Group"
                  checked={reportBy === 0} onChange={() => setReportBy(0)} />
                <Form.Check inline type="radio" name="reportBy" label="Step"
                  checked={reportBy === 1} onChange={() => setReportBy(1)} />
              </div>
            </div>
          </div>
        ) : (
          <div className="lpcr-filter-summary" title={filterSummary}>{filterSummary}</div>
        )}

        <div className="lpcr-actions">
          <Button size="sm" variant="light" onClick={() => setFiltersOpen((o) => !o)}>
            {filtersOpen ? "Hide filters" : "Edit filters"}
          </Button>
          {report && (
            <>
              <Button size="sm" variant="secondary" onClick={expandAll} disabled={allBatchIds.length === 0}>Expand all</Button>
              <Button size="sm" variant="secondary" onClick={collapseAll} disabled={openBatches.size === 0}>Collapse all</Button>
            </>
          )}
          <Button size="sm" variant="primary" onClick={generate} disabled={loading || !selCourses.length}>
            {loading ? "…" : "Generate"}
          </Button>
          <Button size="sm" variant="success" onClick={exportXls} disabled={exporting || !report}>
            {exporting ? "…" : "xls"}
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" className="lpcr-alert">{error}</Alert>}

      {loading && !report ? (
        <div className="lpcr-empty"><Spinner animation="border" size="sm" /> Generating report…</div>
      ) : report ? (
        report.courses.length === 0 ? (
          <div className="lpcr-empty">No data for the selected filters.</div>
        ) : (
          report.courses.map((course) => (
            <section key={course.courseId} className="lpcr-course">
              <h4 className="lpcr-course-title">{course.courseName}</h4>
              <CoverageTable
                topics={course.topics}
                centres={course.centres}
                isBatchOpen={isBatchOpen}
                toggleBatch={toggleBatch}
                showCoverage={showCoverage}
                attCols={attCols}
              />
            </section>
          ))
        )
      ) : (
        <div className="lpcr-empty">Choose filters, then click <strong>Generate</strong> to run the report.</div>
      )}
    </div>
  );
};

export default LessonPlanCoverageReport;
